import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ACTION_DONE, ACTION_IDLE, ACTION_INVALID, ACTION_SUBMITTING, isStaleState } from '../recordActionStates';

import WorkFieldsBody from './bodies/WorkFieldsBody';
import useWorkAction from './useWorkAction';
import useWorkDetail from './useWorkDetail';
import {
    DETAIL_TYPE_CONTRACTS,
    DETAIL_TYPE_DECISIONS,
    DETAIL_TYPE_EVALUATIONS,
    DETAIL_TYPE_ISSUES,
    DETAIL_TYPE_OPPORTUNITIES,
    DETAIL_TYPE_PLAN_ACTIVITY,
    DETAIL_TYPE_PROCUREMENTS,
    DETAIL_TYPE_PROJECT_ACTION,
    DETAIL_TYPE_RISKS,
} from './workDetailTypes';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import ArrowLeftIcon from 'Assets/icons/arrow-left.svg';
import ArrowRightIcon from 'Assets/icons/arrow-right.svg';

const isRtl = () => typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

/**
 * ONE pop-up for every row in every Home work section, so a row opens where the
 * user already is instead of sending them into a module that may refuse them.
 *
 * WHAT IT DOES NOT DO: decide what the user may see or do. It renders the fields
 * the server sent, offers the status control only where the payload says
 * `can_update_status`, and offers the module link only where `module_link` is
 * non-null. There is no client-side permission check anywhere in here, and a
 * refusal is a state rather than an error — the same contract
 * `ApprovalDetailModal` holds, deliberately, so Home's two pop-ups behave as one
 * feature.
 *
 * THE FIELD LIST IS UNIFORM BY CONTRACT, which is why `BODIES` maps all nine
 * cases to one component rather than there being nine near-identical files. The
 * detail payload is `title`, `subtitle` and ordered label/value `fields` for
 * every type — a type sends the columns its own schema carries and no others —
 * so a per-type body has nothing type-specific left to render. The registry is
 * kept, keyed by all nine cases, because it is the extension point: a type that
 * later needs more than a field list gets its own body here and nothing else
 * changes.
 *
 * `id` IS A PROP, not a constant, because Home keeps a visited tab MOUNTED
 * (HomeTabs renders on first visit and retains). It is also why this pop-up is
 * mounted ONCE, by HomeSections, rather than once per section: eight mounted
 * modals would give eight elements the same id, with the wrong one answering —
 * the collision `ApprovalDetailModal` documents.
 */

const BODIES = {
    [DETAIL_TYPE_PLAN_ACTIVITY]: WorkFieldsBody,
    [DETAIL_TYPE_PROJECT_ACTION]: WorkFieldsBody,
    [DETAIL_TYPE_RISKS]: WorkFieldsBody,
    [DETAIL_TYPE_ISSUES]: WorkFieldsBody,
    [DETAIL_TYPE_DECISIONS]: WorkFieldsBody,
    [DETAIL_TYPE_OPPORTUNITIES]: WorkFieldsBody,
    [DETAIL_TYPE_PROCUREMENTS]: WorkFieldsBody,
    [DETAIL_TYPE_CONTRACTS]: WorkFieldsBody,
    [DETAIL_TYPE_EVALUATIONS]: WorkFieldsBody,
};

export default function WorkDetailModal({
    modal,
    target,
    detailUrlTemplate,
    statusUrlTemplate,
    detailTypePlaceholder,
    idPlaceholder,
    onClose,
    onActioned,
    id = 'home-work-detail',
}) {
    const ArrowIcon = isRtl() ? ArrowLeftIcon : ArrowRightIcon;

    /* Deliberately NOT initialised from detail.status_id: the control asks for
       a new status, not a re-save of the current one, so it starts blank and
       the submit button stays disabled until the user picks something. */
    const [statusId, setStatusId] = useState('');

    const detailRequest = useWorkDetail({
        urlTemplate: detailUrlTemplate,
        detailTypePlaceholder,
        idPlaceholder,
        detailType: target?.detailType,
        id: target?.id,
    });

    const action = useWorkAction({
        urlTemplate: statusUrlTemplate,
        detailTypePlaceholder,
        idPlaceholder,
        /* The server's refreshed payload replaces the one on screen, so what the
           user may do next is the server's answer rather than a local patch. */
        onUpdated: (updated) => updated && detailRequest.setDetail(updated),
    });

    /* The target is cleared the instant the pop-up starts closing, but flowbite
       is still animating it out — rendering the empty shell then flashes a blank
       box over the page. The last detail is held so the content stays put until
       it is gone. */
    const lastDetailRef = useRef(null);

    if (detailRequest.detail) {
        lastDetailRef.current = detailRequest.detail;
    }

    const detail = detailRequest.detail ?? (target ? null : lastDetailRef.current);

    // A fresh record is a fresh decision: no status selection and no refusal
    // message from the previous one may survive into it.
    useEffect(() => {
        setStatusId('');
        action.reset();
    }, [target?.detailType, target?.id]);

    const Body = useMemo(() => BODIES[detail?.type] ?? WorkFieldsBody, [detail?.type]);

    const statusSelectId = `${id}-status`;

    const close = () => {
        /* A completed write means the section on screen is now wrong, so it is
           re-read rather than left stale — and so does every terminal refusal.
           `isStaleState` ALREADY INCLUDES ACTION_DONE, so this asks it rather
           than naming that state again beside it: two copies of which states
           are stale is how the two come to disagree. */
        if (isStaleState(action.state)) {
            onActioned?.();
        } else {
            onClose?.();
        }
    };

    if (detailRequest.loading) {
        return (
            <Modal id={id} modal={modal} innerContainerClasses="tw-max-w-3xl">
                <LoadingSpinner />
            </Modal>
        );
    }

    /* GONE IS A STATE, NOT AN ERROR. The record was deleted, reassigned, closed
       or unpinned from this version since the section was read — all one 404 —
       so the pop-up says so and offers the way out rather than showing a
       half-record or a stack trace. */
    if (detailRequest.state) {
        return (
            <Modal id={id} modal={modal} innerContainerClasses="tw-max-w-3xl">
                <ModalHeader title={$t('This item is no longer available')} close={close} />
                <Alert type="error">
                    {detailRequest.gone
                        ? $t('It may have been deleted or reassigned since this page loaded. Refresh to see the current list.')
                        : $t('We could not load this item. Please refresh to try again.')}
                </Alert>
                <div className="modal__footer">
                    <Button variant="secondary" onClick={close}>
                        {$t('Close')}
                    </Button>
                </div>
            </Modal>
        );
    }

    // Nothing renders until the record resolves. There is no error boundary in
    // this app and this mounts on the landing page, so a half-built shell is not
    // worth the risk of a throw.
    if (!detail) {
        return <Modal id={id} modal={modal} innerContainerClasses="tw-max-w-3xl" />;
    }

    const statusOptions = detail.status_options ?? [];
    const canUpdateStatus = !!detail.can_update_status && statusOptions.length > 0;

    const invalid = action.state === ACTION_INVALID;
    const refused = ![ACTION_IDLE, ACTION_SUBMITTING, ACTION_DONE].includes(action.state);

    return (
        <Modal id={id} modal={modal} innerContainerClasses="tw-max-w-3xl">
            <ModalHeader title={detail.title} subtitle={detail.subtitle} close={close} />

            <Body detail={detail} />

            {canUpdateStatus && (
                <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-1.5">
                    <label className="text-input__label" htmlFor={statusSelectId}>
                        {$t('Status')}
                    </label>
                    <select
                        className="tw-border-0 select-input"
                        id={statusSelectId}
                        name="status_id"
                        value={statusId}
                        onChange={(e) => setStatusId(e.target.value)}
                        aria-invalid={invalid}
                        aria-describedby={invalid ? `${statusSelectId}-error` : undefined}
                    >
                        <option value="">{$t('Select a status')}</option>
                        {statusOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.title}
                            </option>
                        ))}
                    </select>
                    {invalid && (
                        <p id={`${statusSelectId}-error`} role="alert" className="tw-text-xs tw-text-red">
                            {action.message}
                        </p>
                    )}
                </div>
            )}

            {refused && !invalid && (
                <div className="tw-mt-4">
                    <Alert type="error">{action.message}</Alert>
                </div>
            )}

            <div className="modal__footer">
                <Button variant="secondary" onClick={close}>
                    {$t('Close')}
                </Button>

                {/* OFFERED ONLY WHERE THE SERVER SENT ONE. A null `module_link`
                    means no screen in that module would admit this user to this
                    record — a department-scoped user on a record with no source
                    page, or a revenue contract behind `department.scope` — so
                    they get the record and no control, rather than a button that
                    403s. */}
                {!!detail.module_link && (
                    <Button variant="outline" onClick={() => window.location.assign(detail.module_link)}>
                        {$t('Open in {{module}}', { module: detail.module_label })}
                        <ArrowIcon className="tw-w-3 tw-h-3 tw-ms-1" />
                    </Button>
                )}

                {/* A validation failure is CORRECTABLE, so the control stays:
                    hiding it on a 422 tells the user what to fix and then takes
                    away the button that submits the fix. Every other refusal is
                    terminal and the control goes. */}
                {canUpdateStatus && (!refused || invalid) && (
                    <Button variant="primary" disabled={action.submitting || !statusId} onClick={() => action.submit(detail.type, detail.id, Number(statusId))}>
                        {$t('Update status')}
                    </Button>
                )}
            </div>
        </Modal>
    );
}
